import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './PayrollBreakdown.css';

const PayrollBreakdown = () => {
  const [payrollPeriods, setPayrollPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  // Add state for period statuses
  const [periodStatuses, setPeriodStatuses] = useState({});

  useEffect(() => {
    // Fetch available payroll periods when the component mounts
    fetchPayrollPeriods();
  }, []);

  // Function to determine the status of each period based on default payment completeness
  const getPayrollPeriodStatus = (period, transactions) => {
    if (!transactions || transactions.length === 0) {
      return 'No Data';
    }
    
    // Filter transactions for this specific period
    const periodTransactions = transactions.filter(transaction => {
      if (transaction['Pay date'] && transaction['Pay period']) {
        const payDate = new Date(transaction['Pay date']);
        const year = payDate.getFullYear();
        const formattedPeriod = `${year} - ${transaction['Pay period']}`;
        return formattedPeriod === period;
      }
      return false;
    });
    
    if (periodTransactions.length === 0) {
      return 'No Transactions';
    }

    console.log("period transactions for period", period, periodTransactions);
    
    // Check for empty Default Payment fields
    const emptyDefaultPayments = periodTransactions.filter(transaction => {
      return !transaction['Checked'] ||
            transaction['Checked'] === '' ||
            transaction['Checked'] === 'No' ||
            !transaction['Default Payment'] || 
            transaction['Default Payment'] === '' || 
            transaction['Default Payment'] === 'N/A' ||
            (typeof transaction['Default Payment'] === 'object' && 
              Object.keys(transaction['Default Payment']).length === 0);
    });
    
    // Calculate status based on empty default payments
    if (emptyDefaultPayments.length === 0) {
      return 'Complete';
    } else if (emptyDefaultPayments.length === periodTransactions.length) {
      return 'Not Started';
    } else {
      // Some transactions have default payments, but others don't
      return "In Progress";
    }
  };

  // Helper function to get the CSS class for each status
  const getStatusClass = (status) => {
    switch (status.toLowerCase()) {
      case 'complete': return 'status-complete';
      case 'in progress': return 'status-processing';
      case 'not started': return 'status-not-started';
      default: return '';
    }
  };

  const fetchPayrollPeriods = async () => {
    try {
      setLoading(true);
      
      // Fetch all payroll transactions
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/show-all`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const transactions = await response.json();
      
      // Extract unique pay periods by combining year and pay period
      const uniquePeriods = new Set();
      
      transactions.forEach(transaction => {
        if (transaction['Pay date'] && transaction['Pay period']) {
          // Extract year from Pay date
          const payDate = new Date(transaction['Pay date']);
          const year = payDate.getFullYear();
          
          // Combine year and pay period
          const formattedPeriod = `${year} - ${transaction['Pay period']}`;
          uniquePeriods.add(formattedPeriod);
        }
      });
      
      // Convert Set to Array
      const periodsArray = Array.from(uniquePeriods);
      
      // Sort periods in descending order (newest first)
      const sortedPeriods = [...periodsArray].sort((a, b) => {
        return b.localeCompare(a);
      });
      
      setPayrollPeriods(sortedPeriods);

      // Simulate fetching statuses - replace with actual API call
      const statuses = {};
      sortedPeriods.forEach(period => {
        statuses[period] = getPayrollPeriodStatus(period, transactions);
      });
      setPeriodStatuses(statuses);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching payroll periods:', error);
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/import`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (response.ok) {
          alert('Payroll transactions imported successfully');
          // Refresh the payroll periods list
          fetchPayrollPeriods();
        } else {
          const errorMessage = await response.text();
          alert(`Failed to import payroll transactions: ${errorMessage}`);
        }
      } catch (error) {
        console.error('Error importing payroll transactions:', error);
        alert('Error importing payroll transactions');
      }
    }
  };

  return (
    <div className="payroll-breakdown-page">
      <div className="page-header">
        <h1>Payroll Breakdown</h1>
        <div className="upload-container">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            ref={fileInputRef}
          />
          <button 
            className="upload-button" 
            onClick={() => fileInputRef.current.click()}
          >
            Upload Payroll File
          </button>
        </div>
      </div>
      
      <div className="periods-container">
        <h2>
          Payroll Periods
          <span className="total-count">({payrollPeriods.length} periods)</span>
        </h2>
        {loading ? (
          <p>Loading payroll periods...</p>
        ) : (
          <table className="periods-table">
            <thead>
              <tr>
                <th>Pay Period</th>
                <th>Status</th>
                {/* <th>Transactions</th>
                <th>Actions</th> */}
              </tr>
            </thead>
            <tbody>
              <tr className="all-periods">
                <td>
                  <Link to="/payroll/period/all" className="period-link">
                    All Payroll Periods
                  </Link>
                </td>
                <td>-</td>
                {/* Additional fields possible */}
                {/* <td>All</td>
                <td>-</td> */}
              </tr>
              {payrollPeriods.map((period, index) => (
                <tr key={index}>
                  <td>
                    <Link to={`/payroll/period/${encodeURIComponent(period)}`} className="period-link">
                      {period}
                    </Link>
                  </td>
                  <td>
                    <span className={`status ${getStatusClass(periodStatuses[period] || '')}`}>
                      {periodStatuses[period] || 'Unknown'}
                    </span>
                  </td>
                  {/* Additional fields possible */}
                  {/* <td>
                    -
                  </td>
                  <td>
                    <button>View Details</button>
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PayrollBreakdown;